// IELTS Mock Tests page component

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ieltsAPI } from '../services/api';
import { IELTSMockTest } from '../types';

const IELTS: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [tests, setTests] = useState<IELTSMockTest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedType, setSelectedType] = useState<string>('');

  useEffect(() => {
    loadTests();
  }, [selectedType]);

  const loadTests = async () => {
    try {
      const testData = await ieltsAPI.getTests(selectedType || undefined);
      setTests(testData);
    } catch (err: any) {
      setError('Failed to load tests. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTest = async (testId: number) => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      const response = await ieltsAPI.startTest(testId);
      navigate(`/ielts/test/${testId}`);
    } catch (err: any) {
      setError('Failed to start test. Please try again.');
    }
  };

  const getTestTypeColor = (type: string) => {
    switch (type) {
      case 'academic': return 'bg-blue-100 text-blue-800';
      case 'general': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTestTypeDescription = (type: string) => {
    switch (type) {
      case 'academic': return 'For university admission and professional registration';
      case 'general': return 'For immigration and work purposes';
      default: return 'IELTS test';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto text-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
        <button
          onClick={() => navigate('/')}
          className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
        >
          Go Home
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="text-center py-16">
        <h1 className="text-5xl font-bold text-gray-900 mb-6">
          IELTS Mock Exams
        </h1>
        <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
          Practice with full-length IELTS mock exams that replicate the real test experience. 
          Get detailed feedback and band scores to track your progress.
        </p>
      </div>

      {/* Filter Options */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Filter Tests</h2>
        <div className="flex space-x-4">
          <button
            onClick={() => setSelectedType('')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              selectedType === '' 
                ? 'bg-primary-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            All Tests
          </button>
          <button
            onClick={() => setSelectedType('academic')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              selectedType === 'academic' 
                ? 'bg-primary-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Academic
          </button>
          <button
            onClick={() => setSelectedType('general')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              selectedType === 'general' 
                ? 'bg-primary-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            General Training
          </button>
        </div>
      </div>

      {/* Tests Grid */}
      {tests.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📚</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Tests Available</h3>
          <p className="text-gray-600">
            {selectedType 
              ? `No ${selectedType} tests are currently available.` 
              : 'No tests are currently available.'
            }
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tests.map((test) => (
            <div key={test.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getTestTypeColor(test.test_type)}`}>
                  {test.test_type.charAt(0).toUpperCase() + test.test_type.slice(1)}
                </span>
                <span className="text-sm text-gray-500">
                  {new Date(test.created_at).toLocaleDateString()}
                </span>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-2">{test.title}</h3>
              <p className="text-gray-600 mb-4">{test.description}</p>
              
              <div className="mb-4">
                <p className="text-sm text-gray-500 mb-2">{getTestTypeDescription(test.test_type)}</p>
                <div className="flex items-center text-sm text-gray-600">
                  <span className="mr-4">🎧 Listening: 40 min</span>
                  <span className="mr-4">📖 Reading: 60 min</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <span className="mr-4">✍️ Writing: 60 min</span>
                  <span>🎤 Speaking: 15 min</span>
                </div>
              </div>

              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-500">
                  {test.sections.length} sections
                </div>
                <button
                  onClick={() => handleStartTest(test.id)}
                  className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Start Test
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* IELTS Information */}
      <div className="bg-white rounded-lg shadow-md p-8 mt-16">
        <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">About IELTS</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Test Format</h3>
            <ul className="space-y-2 text-gray-600">
              <li>• <strong>Listening:</strong> 40 questions, 40 minutes</li>
              <li>• <strong>Reading:</strong> 40 questions, 60 minutes</li>
              <li>• <strong>Writing:</strong> 2 tasks, 60 minutes</li>
              <li>• <strong>Speaking:</strong> 3 parts, 11-14 minutes</li>
            </ul>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Band Scores</h3>
            <ul className="space-y-2 text-gray-600">
              <li>• <strong>9.0:</strong> Expert user</li>
              <li>• <strong>7.0-8.5:</strong> Good user</li>
              <li>• <strong>5.5-6.5:</strong> Competent user</li>
              <li>• <strong>4.0-5.0:</strong> Modest user</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IELTS;

